import { computed, inject, Injector, signal } from "@angular/core";
import { CommonForm, CommonTextField, CommonUrlField, CommonUrlFieldConfig, UtilityService } from "@eforall/common";
import { Venue } from "@interfaces";
import { VenueDialogDataService } from "../venue-dialog-data.service";

export interface VenueEditFormValue {
	internalNotes?: string,
	publicNotes?: string,
	displayedName: string,
}


export class VenueEditFormService {

	public readonly form = new CommonForm<VenueEditFormValue>(inject(Injector));
	public venueDialogDataService = inject(VenueDialogDataService);
	public readonly util = inject(UtilityService);

	public values = signal<VenueEditFormValue>({
		publicNotes: undefined,
		internalNotes: undefined,
		displayedName: '',
	});


	public setValues(venue: Venue) {
		// Reset form state to ensure pending values are set to null before setting new values
		this.form.resetAll();
		if (!venue) {
			this.values.set({
				publicNotes: undefined,
				internalNotes: undefined,
				displayedName: '',
			});
			return;
		}
		const { publicNotes, internalNotes, displayedName, website, officialName } = venue;

		this.values.set({ publicNotes, internalNotes, displayedName });
	}



	readonly displayedName = computed<CommonTextField>(() => ({
		config: signal({ label: 'Displayed Name', min: 5, max: 50, fixCase: 'title-case', isRequired: true, multiLine: false, isSpanish: false, isEnabled: computed(() => this.venueDialogDataService.canEdit()) }),
		actualValue: computed(() => this.values().displayedName || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'displayedName',
		form: this.form,
	}));
	

	readonly publicNotes = computed<CommonTextField>(() => ({
		config: signal({ label: 'Public Notes', min: 0, max: 200, fixCase: 'sentence-case', isRequired: false, multiLine: true, isSpanish: false, isEnabled: computed(() => this.venueDialogDataService.canEdit()) }),
		actualValue: computed(() => this.values().publicNotes || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'publicNotes',
		form: this.form,
	}));

	readonly internalNotes = computed<CommonTextField>(() => ({
		config: signal({ label: 'Internal Notes', min: 0, max: 200, fixCase: 'sentence-case', isRequired: false, multiLine: true, isSpanish: false, isEnabled: computed(() => this.venueDialogDataService.canEdit()) }),
		actualValue: computed(() => this.values().internalNotes || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'internalNotes',
		form: this.form,
	}));

}